import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from 'shared/hooks/utils';
import { DebtorFileTypeSchema } from 'types';
import { z } from 'zod/v4';

const DebtorFileSchema = z.object({
  link: z.string(),
  filename: z.string(),
  type: DebtorFileTypeSchema,
});

const DebtorFilesSchema = z.object({
  files: z.array(DebtorFileSchema),
});

export type DebtorFile = z.infer<typeof DebtorFileSchema>;
export type DebtorFiles = z.infer<typeof DebtorFilesSchema>;

async function loadDebtorFilesFn(axiosInstance: Promise<AxiosInstance>, { queryKey }: any) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`/debtors/${queryKey[1]}/files`);
  return DebtorFilesSchema.parse(data);
}

const emptyDebtorFiles: DebtorFiles = { files: [] };

export function useLoadDebtorFiles(debtorId: string, { enabled }: { enabled: boolean }) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<DebtorFiles, ApiError>({
    queryKey: ['debtors', debtorId, 'files'] as const,
    queryFn: context => loadDebtorFilesFn(axiosInstance, context),
    placeholderData: previousData => previousData ?? emptyDebtorFiles,
    enabled,
  });

  return addResourceNameToQueryResult<DebtorFiles, ApiError, 'debtorFiles', 'PreventUndefined'>(
    'debtorFiles',
    queryResult,
    emptyDebtorFiles,
  );
}
